import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, FileText, Image as ImageIcon, Film, Music, FileCode, Check, X, Loader } from 'lucide-react';

/**
 * Cool drag & drop overlay for Swirl
 * Shows a beautiful overlay when user drags files into the browser
 */
export default function DropZoneOverlay({ onFilesDropped, enabled = true }) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const [dragCounter, setDragCounter] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      setDragCounter(prev => prev + 1);
      
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        setIsDragging(true);
      }
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      setDragCounter(prev => {
        const newCount = prev - 1;
        if (newCount === 0) {
          setIsDragging(false);
        }
        return newCount;
      });
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      setIsDragging(false);
      setDragCounter(0);

      const files = [...e.dataTransfer.files];
      
      if (files.length > 0 && onFilesDropped) {
        // Show upload progress
        const initialProgress = files.map((file, i) => ({
          id: `upload-${Date.now()}-${i}`,
          filename: file.name,
          status: 'uploading',
          progress: 0
        }));
        
        setUploadProgress(initialProgress);

        // Trigger the upload callback
        try {
          await onFilesDropped(files, (fileIndex, progress) => {
            setUploadProgress(prev => {
              const updated = [...prev];
              if (updated[fileIndex]) {
                updated[fileIndex].progress = progress;
                if (progress === 100) {
                  updated[fileIndex].status = 'complete';
                }
              }
              return updated;
            });
          });

          // Clear progress after delay
          setTimeout(() => {
            setUploadProgress([]);
          }, 3000);
        } catch (error) {
          console.error('Upload error:', error);
          setUploadProgress(prev => 
            prev.map(p => ({ ...p, status: 'error' }))
          );
        }
      }
    };

    document.body.addEventListener('dragenter', handleDragEnter);
    document.body.addEventListener('dragleave', handleDragLeave);
    document.body.addEventListener('dragover', handleDragOver);
    document.body.addEventListener('drop', handleDrop);

    return () => {
      document.body.removeEventListener('dragenter', handleDragEnter);
      document.body.removeEventListener('dragleave', handleDragLeave);
      document.body.removeEventListener('dragover', handleDragOver);
      document.body.removeEventListener('drop', handleDrop);
    };
  }, [enabled, onFilesDropped]);

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
      return ImageIcon;
    }
    if (['mp4', 'mov', 'avi', 'mkv'].includes(ext)) {
      return Film;
    }
    if (['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) {
      return Music;
    }
    if (['pdf', 'doc', 'docx', 'txt'].includes(ext)) {
      return FileText;
    }
    if (['js', 'jsx', 'ts', 'tsx', 'py', 'java'].includes(ext)) {
      return FileCode;
    }
    return FileText;
  };

  return (
    <>
      {/* Drag Overlay */}
      <AnimatePresence>
        {isDragging && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] pointer-events-none"
          >
            {/* Backdrop with gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-cyan-500/10 backdrop-blur-lg" />
            
            {/* Animated border */}
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              className="absolute inset-8 rounded-3xl border-4 border-dashed border-purple-400"
              style={{
                background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.1) 0%, rgba(236, 72, 153, 0.1) 50%, rgba(6, 182, 212, 0.1) 100%)'
              }}
            />

            {/* Center content */}
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="text-center"
              >
                <motion.div
                  animate={{ 
                    scale: [1, 1.1, 1],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{ 
                    duration: 2, 
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="w-32 h-32 mx-auto mb-6 rounded-3xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-2xl"
                >
                  <Upload size={56} className="text-white" />
                </motion.div>

                <h2 className="text-4xl font-bold text-gray-900 mb-3">
                  Drop to add to Swirl
                </h2>
                <p className="text-lg text-gray-600">
                  Images, PDFs, videos, and more
                </p>

                {/* Floating particles */}
                {[...Array(8)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ 
                      opacity: [0, 1, 0],
                      scale: [0, 1, 0],
                      x: [0, Math.random() * 200 - 100],
                      y: [0, Math.random() * 200 - 100]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: i * 0.2,
                      ease: "easeOut"
                    }}
                    className="absolute w-3 h-3 rounded-full bg-purple-400"
                    style={{
                      top: '50%',
                      left: '50%'
                    }}
                  />
                ))}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upload Progress Toast */}
      <AnimatePresence>
        {uploadProgress.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-6 right-6 z-[9998] w-96 max-h-[500px] overflow-y-auto"
          >
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 space-y-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-900">
                  Uploading {uploadProgress.length} {uploadProgress.length === 1 ? 'file' : 'files'}
                </h3>
                <button
                  onClick={() => setUploadProgress([])}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              {uploadProgress.map((item) => {
                const Icon = getFileIcon(item.filename);
                
                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl"
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      item.status === 'complete' 
                        ? 'bg-green-100' 
                        : item.status === 'error'
                        ? 'bg-red-100'
                        : 'bg-purple-100'
                    }`}>
                      {item.status === 'complete' ? (
                        <Check size={20} className="text-green-600" />
                      ) : item.status === 'error' ? (
                        <X size={20} className="text-red-600" />
                      ) : (
                        <Icon size={20} className="text-purple-600" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {item.filename}
                      </p>
                      
                      {item.status === 'uploading' && (
                        <div className="mt-1.5">
                          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${item.progress}%` }}
                              className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                            />
                          </div>
                        </div>
                      )}
                      
                      {item.status === 'complete' && (
                        <p className="text-xs text-green-600 mt-0.5">
                          Upload complete
                        </p>
                      )}
                      
                      {item.status === 'error' && (
                        <p className="text-xs text-red-600 mt-0.5">
                          Upload failed
                        </p>
                      )}
                    </div>

                    {item.status === 'uploading' && (
                      <Loader size={18} className="text-purple-500 animate-spin flex-shrink-0" />
                    )}
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

